import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import Loading from './Loading';
import ErrorToast from './ErrorToast';

/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - selectedIds: number[]
 * - type: 'clientes' | 'imoveis'
 * - onSuccess: () => void
 */
const BulkEditEtiquetasModal = ({ isOpen, onClose, selectedIds = [], type = 'clientes', onSuccess }) => {
  const [etiquetas, setEtiquetas] = useState([]);
  const [addEtiquetaIds, setAddEtiquetaIds] = useState([]);
  const [removeEtiquetaIds, setRemoveEtiquetaIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError('');
    api
      .get('/etiquetas')
      .then((res) => setEtiquetas(res.data || []))
      .catch((e) => setError(e.response?.data?.error || 'Erro ao carregar etiquetas.'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setAddEtiquetaIds([]);
      setRemoveEtiquetaIds([]);
      setError('');
    }
  }, [isOpen]);

  const addOptions = useMemo(() => {
    return etiquetas
      .filter((e) => !removeEtiquetaIds.includes(e.id))
      .map((e) => ({ value: e.id, label: e.nome }));
  }, [etiquetas, removeEtiquetaIds]);

  const removeOptions = useMemo(() => {
    return etiquetas
      .filter((e) => !addEtiquetaIds.includes(e.id))
      .map((e) => ({ value: e.id, label: e.nome }));
  }, [etiquetas, addEtiquetaIds]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload =
        type === 'clientes'
          ? { cliente_ids: selectedIds, add_etiqueta_ids: addEtiquetaIds, remove_etiqueta_ids: removeEtiquetaIds }
          : { imovel_ids: selectedIds, add_etiqueta_ids: addEtiquetaIds, remove_etiqueta_ids: removeEtiquetaIds };
      const endpoint = type === 'clientes' ? '/clientes/etiquetas' : '/imoveis/etiquetas';
      await api.post(endpoint, payload);
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      const msg = e.response?.data?.error || 'Erro ao aplicar etiquetas.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 600 }}>
        <button onClick={onClose} className="modal-close">×</button>
        <h2 className="page-title" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>
          Editar Etiquetas em Massa
        </h2>
        <p style={{ marginTop: 0, color: '#555' }}>
          Editando etiquetas para {selectedIds.length} {type === 'clientes' ? 'cliente(s)' : 'imóvel(is)'} selecionado(s)
        </p>

        {error && <ErrorToast message={error} onClose={() => setError('')} />}
        {loading ? (
          <Loading />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-row" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <label>Adicionar Etiquetas</label>
                <select
                  multiple
                  value={addEtiquetaIds}
                  onChange={(e) =>
                    setAddEtiquetaIds(Array.from(e.target.selectedOptions, (opt) => Number(opt.value)))
                  }
                  style={{ width: '100%', minHeight: 140 }}
                >
                  {addOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label>Remover Etiquetas</label>
                <select
                  multiple
                  value={removeEtiquetaIds}
                  onChange={(e) =>
                    setRemoveEtiquetaIds(Array.from(e.target.selectedOptions, (opt) => Number(opt.value)))
                  }
                  style={{ width: '100%', minHeight: 140 }}
                >
                  {removeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
              <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
                Cancelar
              </button>
              <button type="submit" className="button-primary" disabled={submitting}>
                {submitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BulkEditEtiquetasModal;

